import React, { useState, useEffect, useRef } from 'react';
import RequestAccessTab from './RequestAccessTab';
import MyRequestsTab from './MyRequestsTab';
import RequestsPage from '../RequestsPage';
import ToApproveTab from './ToApproveTab';
import { useFeature } from '../../context/AuthContext';
import './requests.css';

const VALID = new Set(['access', 'new', 'mine', 'approve']);

function readTabFromHash() {
  if (typeof window === 'undefined') return 'access';
  const m = window.location.hash.match(/sub=([a-z]+)/i);
  return m && VALID.has(m[1]) ? m[1] : 'access';
}

function writeTabToHash(sub) {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  params.set('sub', sub);
  const hash = `#${params.toString()}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
  }
}

export default function RequestsHubPage() {
  const proposalsOn = useFeature('tool_proposal_approval');
  const [active, setActive] = useState(readTabFromHash);
  useEffect(() => { writeTabToHash(active); }, [active]);
  useEffect(() => {
    if (!proposalsOn && (active === 'new' || active === 'approve')) setActive('access');
  }, [proposalsOn, active]);

  const tabs = [
    { key: 'access', label: 'Request access' },
    ...(proposalsOn ? [{ key: 'new', label: 'Propose new tool' }, { key: 'approve', label: 'To approve' }] : []),
    { key: 'mine', label: 'My requests' },
  ];

  // ARIA tabs: one Tab stop (the active tab); arrows, Home and End move the
  // selection and focus together (automatic activation).
  const tabRefs = useRef({});
  const onTabKeyDown = (e) => {
    const i = tabs.findIndex((t) => t.key === active);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    const key = tabs[next].key;
    setActive(key);
    tabRefs.current[key]?.focus();
  };

  return (
    <div className="main-content-inner page-fill">
      <div className="page-head page-title-row">
        <h1 className="page-title">Tool requests</h1>
        <span className="page-subtitle">
          {proposalsOn
            ? 'Request access to a tool, or propose a new one.'
            : 'Request access to a tool in the catalog.'}
        </span>
      </div>
      <div className="admin-subtabs admin-subtabs--scroll" role="tablist" aria-label="Request type">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`rq-tab-${t.key}`}
            ref={(el) => { tabRefs.current[t.key] = el; }}
            aria-selected={active === t.key}
            aria-controls="rq-tabpanel"
            tabIndex={active === t.key ? 0 : -1}
            className={`admin-subtab ${active === t.key ? 'is-active' : ''}`}
            onClick={() => setActive(t.key)}
            onKeyDown={onTabKeyDown}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="page-scroll-body" role="tabpanel" id="rq-tabpanel" aria-labelledby={`rq-tab-${active}`}>
        {active === 'access' && <RequestAccessTab />}
        {active === 'new' && proposalsOn && <RequestsPage />}
        {active === 'approve' && proposalsOn && <ToApproveTab />}
        {active === 'mine' && <MyRequestsTab />}
      </div>
    </div>
  );
}
